/**
 * Qidiruv va filtr (TZ §10.6).
 *
 * Filtr holati URL'da turadi — sahifani ulashish mumkin va orqaga
 * tugmasi tabiiy ishlaydi.
 */

import { t, tp } from "../i18n";
import { useEffect, useRef, useState } from "react";
import { api, getCached, searchQueryToParams, type SearchQuery } from "../api";
import { CardList, CardListSkeleton, Chip, EmptyState, ErrorView } from "../components";
import { IconFilter } from "../icons";
import {
  fieldLabel, fundingLabel, levelLabel, typeLabel,
} from "../format";
import { navigate } from "../router";
import type { FeedResponse, TaxonomyResponse } from "../types";

const SORTS: { key: NonNullable<SearchQuery["sort"]>; label: string }[] = [
  { key: "match", label: t("search.sortMatch") },
  { key: "deadline", label: t("search.sortDeadline") },
  { key: "newest", label: t("search.sortNew") },
];

export default function Search({
  query,
  active = true,
}: {
  query: SearchQuery;
  active?: boolean;
}) {
  const [text, setText] = useState(query.q ?? "");
  const searchCacheKey = `search:${searchQueryToParams({ ...query, limit: 30 }).toString()}`;
  const [data, setData] = useState<FeedResponse | null>(() => getCached<FeedResponse>(searchCacheKey));
  const [taxonomy, setTaxonomy] = useState<TaxonomyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [hasBeenActive, setHasBeenActive] = useState(active);
  const isTypingRef = useRef(false);

  useEffect(() => {
    if (active && !hasBeenActive) {
      setHasBeenActive(true);
    }
  }, [active, hasBeenActive]);

  useEffect(() => {
    setText(query.q ?? "");
  }, [query.q]);

  // Jonli qidiruv: 250ms debounce
  useEffect(() => {
    if (!isTypingRef.current) return;
    const timer = setTimeout(() => {
      if ((query.q ?? "") !== text.trim()) {
        update({ q: text.trim() || undefined });
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [text]);

  useEffect(() => {
    if (!hasBeenActive) return;
    api.taxonomy().then(setTaxonomy).catch(() => setTaxonomy(null));
  }, [hasBeenActive]);

  useEffect(() => {
    if (!hasBeenActive) return;
    let cancelled = false;
    const cached = getCached<FeedResponse>(searchCacheKey);
    if (cached) {
      setData(cached);
    } else {
      setData(null);
    }
    setError(null);
    api
      .search({ ...query, limit: 30 })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (!cancelled && !cached) {
          setError(err instanceof Error ? err.message : t("search.failed"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(query)]);

  function update(patch: Partial<SearchQuery>) {
    const next: SearchQuery = { ...query, ...patch };
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "" || value === false) {
        delete next[key as keyof SearchQuery];
      }
    }
    navigate({ name: "search", query: next });
  }

  function toggle(key: "no_ielts" | "no_experience" | "no_fee") {
    update({ [key]: query[key] ? undefined : true } as Partial<SearchQuery>);
  }

  const activeCount = Object.keys(query).filter(
    (k) => k !== "q" && k !== "sort" && k !== "offset" && k !== "limit",
  ).length;

  return (
    <div className="screen">
      <form
        className="search-form"
        onSubmit={(e) => {
          e.preventDefault();
          update({ q: text.trim() || undefined });
        }}
      >
        <input
          className="input"
          value={text}
          placeholder={t("search.placeholder")}
          onChange={(e) => {
            isTypingRef.current = true;
            setText(e.target.value);
          }}
        />
        <button className="btn" type="submit">
          {t("search.submit")}
        </button>
      </form>

      <div className="chip-row">
        <Chip active={showFilters} onClick={() => setShowFilters(!showFilters)}>
          <IconFilter size={15} />
          {t("search.filters")}{activeCount ? ` (${activeCount})` : ""}
        </Chip>
        <Chip active={Boolean(query.no_ielts)} onClick={() => toggle("no_ielts")}>
          {t("search.quickNoIelts")}
        </Chip>
        <Chip
          active={Boolean(query.no_experience)}
          onClick={() => toggle("no_experience")}
        >
          {t("search.quickNoExperience")}
        </Chip>
        <Chip active={Boolean(query.no_fee)} onClick={() => toggle("no_fee")}>
          {t("search.quickNoFee")}
        </Chip>
      </div>

      {showFilters && taxonomy ? (
        <div className="filters">
          <FilterGroup
            title={t("search.filterType")}
            options={taxonomy.types.map((x) => [x.key, typeLabel(x.key, x.label)])}
            value={query.type}
            onPick={(value) => update({ type: value })}
          />
          <FilterGroup
            title={t("search.filterLevel")}
            options={taxonomy.degree_levels.map((d) => [d, levelLabel(d)])}
            value={query.degree}
            onPick={(value) => update({ degree: value })}
          />
          <FilterGroup
            title={t("search.filterFunding")}
            options={taxonomy.funding.map((f) => [f, fundingLabel(f)])}
            value={query.funding}
            onPick={(value) => update({ funding: value })}
          />
          {/*
            Qit'a — `REGIONS` (hashtag hududlari) dan boshqa o'lchov.
            Taxonomy'dan keladi, frontend ro'yxatni qotirmaydi.
          */}
          <FilterGroup
            title={t("search.filterContinent")}
            options={(taxonomy.continents ?? []).map((c) => [c.key, c.label])}
            value={query.continent}
            onPick={(value) => update({ continent: value })}
          />
          <FilterGroup
            title={t("search.filterField")}
            options={taxonomy.fields.map((f) => [f, fieldLabel(f)])}
            value={query.field}
            onPick={(value) => update({ field: value })}
          />
          <div className="filter-group">
            <h3>{t("search.sort")}</h3>
            <div className="chip-row">
              {SORTS.map((sort) => (
                <Chip
                  key={sort.key}
                  active={(query.sort ?? "match") === sort.key}
                  onClick={() => update({ sort: sort.key })}
                >
                  {sort.label}
                </Chip>
              ))}
            </div>
          </div>
          {activeCount ? (
            <button
              className="link-btn"
              type="button"
              onClick={() => navigate({ name: "search", query: { q: query.q } })}
            >
              {t("search.clearFilters")}
            </button>
          ) : null}
        </div>
      ) : null}

      {error ? <ErrorView message={error} /> : null}
      {!error && !data ? <CardListSkeleton count={4} /> : null}

      {data ? (
        data.items.length ? (
          <>
            <p className="muted small">{tp("feed.results", data.total)}</p>
            <CardList items={data.items} source="search" />
          </>
        ) : (
          <EmptyState
            title={t("search.empty")}
            hint={t("search.emptyHint")}
            action={
              activeCount ? (
                <button
                  className="btn"
                  type="button"
                  onClick={() => navigate({ name: "search", query: {} })}
                >
                  {t("search.clearFilters")}
                </button>
              ) : undefined
            }
          />
        )
      ) : null}
    </div>
  );
}

function FilterGroup({
  title,
  options,
  value,
  onPick,
}: {
  title: string;
  options: [string, string][];
  value: string | undefined;
  onPick: (value: string | undefined) => void;
}) {
  return (
    <div className="filter-group">
      <h3>{title}</h3>
      <div className="chip-row">
        {options.map(([key, label]) => (
          <Chip
            key={key}
            active={value === key}
            onClick={() => onPick(value === key ? undefined : key)}
          >
            {label}
          </Chip>
        ))}
      </div>
    </div>
  );
}
